import { useMemo } from 'react';

import {
  DescriptionIcon,
  ErrorIcon,
  Table,
  Text,
  type TypeTableOverlayRenderer,
  type TypeTableProps,
} from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { NO_ROWS_MESSAGE, NoRowsStatusType, type TypeNoRowsStatusKey } from '~/constants';

import styles from './KitchenTable.module.scss';

const EmptyOverlayIcon = ({ rowStatusType }: { rowStatusType: TypeNoRowsStatusKey }) => {
  switch (rowStatusType) {
    case NoRowsStatusType.INIT:
      return <DescriptionIcon color="gray400" size={32} />;
    case NoRowsStatusType.EMPTY:
      return <ErrorIcon color="gray400" size={32} />;
    default:
      return null;
  }
};

/** KitchenTable 자체 선언 빈 목록 오버레이 (ag-grid NoRowsOverlay와 독립) */
const EmptyOverlay = ({ rowStatusType }: { rowStatusType: TypeNoRowsStatusKey }) => {
  return (
    <div role="presentation" className={styles['empty-overlay']}>
      <EmptyOverlayIcon rowStatusType={rowStatusType} />
      <Text color="disabled" size="small" weight="semi-bold">
        {NO_ROWS_MESSAGE[rowStatusType]}
      </Text>
    </div>
  );
};

type TypeKitchenTableProps<TData> = Omit<TypeTableProps<TData>, 'data'> & {
  /** null이면 검색 전(데이터 미요청) 상태. 내부에서 `?? []`로 처리한다. */
  data: TData[] | null;
};

/** kitchen `Table` 공통 래퍼 */
const KitchenTable = <TData,>({
  data,
  overlayRenderer,
  loading,
  error,
  className,
  ...rest
}: TypeKitchenTableProps<TData>) => {
  const rows = data ?? [];

  const emptyOverlayRenderer = useMemo<TypeTableOverlayRenderer<TData>[]>(() => {
    if (overlayRenderer) return overlayRenderer;
    // 로딩/에러는 kitchen 기본 UI에 위임 (overlayRenderer가 매칭되면 loading/error를 덮어쓰므로 빈 배열 반환)
    if (loading || error) return [];

    return [
      {
        when: () => data === null,
        render: <EmptyOverlay rowStatusType={NoRowsStatusType.INIT} />,
      },
      {
        when: () => data?.length === 0,
        render: <EmptyOverlay rowStatusType={NoRowsStatusType.EMPTY} />,
      },
    ];
  }, [overlayRenderer, loading, error, data]);

  return (
    <Table<TData>
      data={rows}
      loading={loading}
      error={error}
      overlayRenderer={emptyOverlayRenderer}
      className={clsx(styles['kitchen-table'], className)}
      {...rest}
    />
  );
};

export default KitchenTable;
